/**
 * Helpers purs Meals — Phase 1, incrément 10.
 *
 * Logique testable (sans DOM ni store) pour :
 *   - calcul des dates de la semaine en cours,
 *   - catégorisation d'ingrédients (port direct `catForIng` proto l. 869),
 *   - formatage de quantité (port direct `fmtQ` proto l. 860),
 *   - agrégation des courses depuis les repas planifiés
 *     (port `syncGrocery` proto l. 870).
 */
import type { GroceryItem, Meal } from '@nestor/shared-types';

// ── catForIng — port direct ─────────────────────────────────────────

export interface IngredientCategory {
  cat: string;
  /** Couleur de fond du chip (hex). Aligné sur la palette du proto. */
  color: string;
}

interface CategoryRule {
  pattern: RegExp;
  cat: string;
  color: string;
}

const CATEGORY_RULES: readonly CategoryRule[] = [
  {
    pattern:
      /poulet|viande|boeuf|bœuf|porc|jambon|lardons|steak|saumon|poisson|crevette|thon|bacon|charcuterie/,
    cat: 'Viandes & Poissons',
    color: '#C49060',
  },
  {
    pattern:
      /carotte|oignon|poireau|navet|courgette|tomate|salade|légume|ail|pomme de terre|champignon|céleri|aubergine|poivron/,
    cat: 'Légumes',
    color: '#7A9E8E',
  },
  {
    pattern: /pomme|banane|citron|orange|fruit|fraise|cerise|poire/,
    cat: 'Fruits',
    color: '#D27C63',
  },
  {
    pattern:
      /lait|crème|beurre|fromage|yaourt|mozzarella|gruyère|raclette|parmesan|mascarpone|cheddar|œuf|oeuf/,
    cat: 'Crémerie',
    color: '#9B7CB6',
  },
  {
    pattern: /pain|farine|pâte|biscuit|brioche|levure/,
    cat: 'Boulangerie',
    color: '#C47A7A',
  },
  {
    pattern: /pâtes|riz|quinoa|lentille|pois|haricot|nouille/,
    cat: 'Épicerie',
    color: '#5A7A6C',
  },
  {
    pattern:
      /huile|sauce|bouillon|moutarde|vinaigre|sel|poivre|herbe|épice|curry|soja|thym|basilic|persil|sucre/,
    cat: 'Condiments',
    color: '#5B8DB8',
  },
  {
    pattern: /eau|jus|lait de coco|vin|bière/,
    cat: 'Boissons',
    color: '#5B8DB8',
  },
];

export function categoryForIngredient(name: string): IngredientCategory {
  const n = name.toLowerCase();
  for (const rule of CATEGORY_RULES) {
    if (rule.pattern.test(n)) return { cat: rule.cat, color: rule.color };
  }
  return { cat: 'Épicerie', color: '#5A7A6C' };
}

// ── fmtQ — port direct (proto l. 860) ───────────────────────────────

/**
 * Formate une quantité avec gestion automatique kg/L. `bs` = portions
 * de la recette d'origine, `ts` = portions cibles. Le proto les met
 * égales dans `syncGrocery` (pas de scaling) mais on garde la
 * signature pour rester compatible si on ajoute le scaling Phase 2.
 */
export function formatQuantity(q: number, u: string, bs: number, ts: number): string {
  const s = q * (ts / bs);
  if (u === 'g' && s >= 1000) return `${(s / 1000).toFixed(1).replace(/\.0$/, '')}kg`;
  if (u === 'ml' && s >= 1000) return `${(s / 1000).toFixed(1).replace(/\.0$/, '')}L`;
  if (s < 1) return `${s.toFixed(1)} ${u}`;
  return `${Math.round(s).toString()} ${u}`;
}

// ── weekDates — port direct (proto l. 1829) ─────────────────────────

export interface WeekDay {
  d: number;
  /** Libellé court (Lun, Mar…). */
  label: string;
}

const DAY_SHORT_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'] as const;
const DAY_SHORT_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/**
 * Renvoie 7 entrées { d, label } pour la semaine en cours
 * (Lundi → Dimanche). Lang = 'fr' ou 'en' (autres langues → fr,
 * fidèle au proto l. 1828).
 */
export function weekDates(now: Date, lang: string): WeekDay[] {
  const labels = lang === 'en' ? DAY_SHORT_EN : DAY_SHORT_FR;
  // Le proto démarre la semaine au lundi : i+1 (lundi = index 1).
  return Array.from({ length: 7 }, (_, i) => {
    const dayOfWeek = i === 6 ? 0 : i + 1; // 6 → dimanche (index 0)
    const d = new Date(now);
    d.setDate(now.getDate() - now.getDay() + i + 1);
    return { d: d.getDate(), label: labels[dayOfWeek]! };
  });
}

// ── Agrégation grocery — port `syncGrocery` (proto l. 870) ──────────

export interface AggregatedIngredient {
  name: string;
  quantity: number;
  unit: string;
  /** `bs` du repas qui a contribué le plus récemment (référence pour fmt). */
  bs: number;
}

/**
 * Agrège les ingrédients de tous les repas planifiés (lunch + dinner +
 * legacy `m`). Les ingrédients de même nom + même unité sont
 * additionnés ; sinon une nouvelle entrée est créée.
 *
 * Pure : ne touche pas au store, ne génère pas d'IDs. La conversion
 * en `GroceryItem[]` se fait via `buildGroceryFromMeals`.
 */
export function aggregateIngredients(meals: readonly Meal[]): AggregatedIngredient[] {
  const agg = new Map<string, AggregatedIngredient>();
  for (const meal of meals) {
    if (!meal.ing || meal.ing.length === 0) continue;
    // Vérifie qu'il y a au moins un repas planifié (sinon ne rien
    // agréger : un meal vide n'a pas d'ingrédients de toute façon).
    const hasPlannedMeal = !!(meal.lunch?.trim() || meal.dinner?.trim() || meal.m.trim());
    if (!hasPlannedMeal) continue;
    for (const ing of meal.ing) {
      // Alpha (item 9) : la base de recettes complète fournit des
      // ingrédients « plats » sans quantité ni unité — on les garde
      // (listés par nom seul dans les courses) au lieu de les jeter.
      if (!ing.n) continue;
      const key = `${ing.n.toLowerCase().trim()}|${ing.u ?? ''}`;
      const existing = agg.get(key);
      if (existing) {
        existing.quantity += ing.q ?? 0;
      } else {
        agg.set(key, {
          name: ing.n,
          quantity: ing.q ?? 0,
          unit: ing.u ?? '',
          bs: meal.bs || 4,
        });
      }
    }
  }
  return [...agg.values()];
}

/**
 * Construit la liste de `GroceryItem` auto-générés depuis les repas.
 * Les items manuels (`src: 'manual'`) doivent être préservés par le
 * caller — cette fonction renvoie uniquement la partie auto.
 *
 * `nextId` est un générateur passé par le caller (typiquement le
 * `nextEntryId('gr')` du store) pour rester déterministe en tests.
 */
export function buildGroceryFromMeals(meals: readonly Meal[], nextId: () => string): GroceryItem[] {
  return aggregateIngredients(meals).map((agg) => ({
    id: nextId(),
    // Ingrédient sans quantité (base complète) → nom seul.
    text:
      agg.quantity > 0 && agg.unit
        ? `${agg.name} — ${formatQuantity(agg.quantity, agg.unit, agg.bs, agg.bs)}`
        : agg.name,
    done: false,
    src: 'recipe',
  }));
}

// ── Rayons de supermarché (alpha) ────────────────────────────────────

/**
 * Ordre de parcours type d'un supermarché — les groupes de la liste de
 * courses suivent cet ordre pour limiter les allers-retours en rayon.
 */
export const RAYON_ORDER: readonly string[] = [
  'Fruits',
  'Légumes',
  'Viandes & Poissons',
  'Crémerie',
  'Boulangerie',
  'Épicerie',
  'Condiments',
  'Boissons',
];

export interface GroceryGroup {
  cat: string;
  color: string;
  items: GroceryItem[];
}

/**
 * Regroupe les articles par famille d'aliments (`categoryForIngredient`
 * sur le texte de l'article) et ordonne les groupes comme les rayons
 * d'un supermarché (`RAYON_ORDER`). Catégorie inconnue → Épicerie.
 */
export function groupGroceryByRayon(items: readonly GroceryItem[]): GroceryGroup[] {
  const map = new Map<string, GroceryGroup>();
  for (const item of items) {
    const { cat, color } = categoryForIngredient(item.text);
    const group = map.get(cat) ?? { cat, color, items: [] };
    group.items.push(item);
    map.set(cat, group);
  }
  return [...map.values()].sort((a, b) => {
    const ia = RAYON_ORDER.indexOf(a.cat);
    const ib = RAYON_ORDER.indexOf(b.cat);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
}
